import * as yup from 'yup';
import { Clock, Send } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { isPast } from 'date-fns';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';

import {
  GetMessageQuery as GetMessage,
  GetMessageRecipientsQuery as GetMessageRecipients,
} from '__generated__/graphql';

import { DateTimeForm } from '../../Common/DateRangeForm';
import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import CancelScheduledMessageMutation from './CancelScheduledMessageMutation';
import ContactPicker, { Participant } from '../../Participants/Contacts/ContactPicker';
import SendMessageMutation from './SendMessageMutation';
import UI from '../../../common/UI';
import useConfirmModal from '../../../common/useConfirmModal';
import useLocale from '../../../common/useLocale';
import useMutation from '../../../api/useMutation';
import useValidation from '../../../common/useValidation';

type Message = GetMessage['project']['message'];
type Recipients = GetMessageRecipients['project']['message'];

const sendMessageSchema = yup.object({
  scheduled_at: yup.string().ensure().required(),
});

export interface SendMessageFormProps {
  message: Message;
  messageSent?: boolean;
  recipients: Recipients;
}

const SendMessageForm = ({ message, messageSent, recipients }: SendMessageFormProps) => {
  const { t } = useTranslation();
  const { formatInteger, formatDate, parseDate, formatRelativeDate } = useLocale();
  const notifier = useNotifier();

  const [sendNow, setSendNow] = useState<boolean | null>(message.scheduled_at === null ? null : false);
  const [sendToAll, setSendToAll] = useState<boolean>(recipients.next_recipient_count > 0);
  const [participant, setParticipant] = useState<Participant | null>(null);

  const getValues = (message: Partial<Message>) => ({
    scheduled_at: message.scheduled_at,
  });

  const form = useForm({
    values: getValues(message),
  });

  const [sendMessage, sendMessageResult] = useMutation(SendMessageMutation, {
    variables: {
      input: {
        id: message.id,
        participant: sendNow && !sendToAll && participant ? { id: participant.id } : null,
        scheduled_at: sendNow === false ? form.values.scheduled_at : null,
      },
    },
    onCompleted: ({ sendMessage }) => {
      form.reset(getValues(sendMessage));
    },
  });

  const [cancelSchedule, cancelScheduleResult] = useMutation(CancelScheduledMessageMutation, {
    variables: { id: message.id },
    onCompleted: ({ cancelScheduledMessage }) => {
      form.reset(getValues(cancelScheduledMessage));
      setSendNow(null);
      setSendToAll(true);

      notifier.success(t('notifications.message_saved'));
    },
  });

  const { valid: hasValidForm, errors } = useValidation({
    schema: sendMessageSchema,
    values: form.values,
    externalErrors: getServerErrors(sendMessageResult.error),
  });

  const [confirmSendMessage] = useConfirmModal({
    title: t('send_message'),
    body: t(sendNow ? 'send_message_now_confirmation' : 'send_message_scheduled_confirmation'),
    onConfirm: () => sendMessage(),
  }, [sendNow]);

  const hasValidRecipient = (sendToAll === true && recipients.next_recipient_count > 0)
    || (sendToAll === false && participant !== null);

  const canSendEmail = !message.draft && (
    (sendNow === true && hasValidRecipient)
    || (sendNow === false && hasValidForm)
  );

  const scheduledAt = message.scheduled_at
    ? parseDate(message.scheduled_at, { timezone: 'UTC' })
    : undefined;

  const isSending = message.remaining_count > 0 || isPast(scheduledAt);

  return (
    <>
      {isSending && (
        <UI.Div>
          <UI.Info icon={<UI.Loader />}>
            <UI.H4>
              {t('message_being_sent')}
            </UI.H4>
            {t('message_being_sent_description')}
          </UI.Info>
        </UI.Div>
      )}

      {message.scheduled_at && !isSending && (
        <UI.Form onSubmit={() => cancelSchedule()}>
          <UI.FormGrid>
            <UI.ServerErrors error={cancelScheduleResult.error} />

            <UI.Info icon={<Clock />}>
              <UI.H4>
                {t('message_will_be_sent_in', {
                  period: formatRelativeDate(
                    formatDate(
                      parseDate(message.scheduled_at, { timezone: 'UTC' }),
                      { format: 'internal_date_time' },
                    ),
                  ),
                })}
              </UI.H4>
              <Trans
                i18nKey="message_scheduled_for"
                values={{
                  date: formatDate(
                    parseDate(message.scheduled_at, { timezone: 'UTC' }),
                    { format: 'display_date_time' },
                  ),
                }}
              >
                <UI.Span sc={{ fontWeight: 500 }} />
              </Trans>
            </UI.Info>

            <UI.HR />

            <UI.Div sc={{ textAlign: 'right' }}>
              <UI.Button
                type="submit"
                disabled={!canSendEmail || cancelScheduleResult.loading}
                sc={{ loading: cancelScheduleResult.loading }}
              >
                {t('cancel_schedule')}
              </UI.Button>
            </UI.Div>
          </UI.FormGrid>
        </UI.Form>
      )}

      {!message.scheduled_at && !isSending && (
        <UI.Form onSubmit={() => confirmSendMessage()}>
          <UI.FormGrid>
            <UI.ServerErrors error={sendMessageResult.error} />

            {messageSent && (
              <UI.Success>
                <UI.H4>
                  {t('notifications.message_sent')}
                </UI.H4>
              </UI.Success>
            )}

            {message.draft && (
              <UI.Warning>
                {t('message_draft_warning')}
              </UI.Warning>
            )}

            <UI.FormGrid sc={{ gutter: 0.25 }}>
              <UI.Radio
                checked={sendNow === true}
                disabled={message.draft}
                onChange={() => setSendNow(true)}
                sc={{ box: true }}
              >
                <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <UI.Div>
                    <UI.Strong sc={{ block: true }}>
                      {t('send_now')}
                    </UI.Strong>
                    {t('send_now_description')}
                  </UI.Div>
                  <UI.Badge sc={{ size: 42, brand: 'secondary.50', color: 'secondary' }}>
                    <UI.Icon sc={{ fontSize: 3 }}>
                      <Send />
                    </UI.Icon>
                  </UI.Badge>
                </UI.FlexContainer>
              </UI.Radio>
              <UI.Radio
                checked={sendNow === false}
                disabled={message.draft}
                onChange={() => setSendNow(false)}
                sc={{ box: true }}
              >
                <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <UI.Div>
                    <UI.Strong sc={{ block: true }}>
                      {t('schedule_message')}
                    </UI.Strong>
                    {t('schedule_message_description')}
                  </UI.Div>
                  <UI.Badge sc={{ size: 42, brand: 'secondary.50', color: 'secondary' }}>
                    <UI.Icon sc={{ fontSize: 3 }}>
                      <Clock />
                    </UI.Icon>
                  </UI.Badge>
                </UI.FlexContainer>
              </UI.Radio>
            </UI.FormGrid>

            {sendNow === false && (
              <DateTimeForm
                form={form}
                errors={errors}
                keys={{ date: 'scheduled_at', time: 'scheduled_at_time' }}
                labels={{ date: t('date'), time: t('time') }}
                minDate={formatDate(new Date(), { timezone: null, format: 'internal_date' })}
                require
              />
            )}

            {sendNow === true && (
              <>
                <UI.InputGroup>
                  <UI.InputLabel htmlFor="MessageSubject">
                    {t('recipients')}
                  </UI.InputLabel>
                  <UI.InputDescription>
                    {t('confirm_send_message_description')}
                  </UI.InputDescription>
                  <UI.Radio
                    checked={sendToAll}
                    onChange={() => setSendToAll(true)}
                    disabled={recipients.next_recipient_count === 0}
                    sc={{ muted: recipients.next_recipient_count === 0 }}
                  >
                    {t('all_recipients')}
                    {' '}
                    (
                    {formatInteger(recipients.next_recipient_count)}
                    )
                  </UI.Radio>
                  <UI.Radio
                    checked={!sendToAll}
                    onChange={() => setSendToAll(false)}
                  >
                    {t('specific_recipient')}
                  </UI.Radio>
                </UI.InputGroup>

                {sendToAll === false && (
                  <UI.InputGroup>
                    <UI.InputLabel htmlFor="MessageParticipant">
                      {t('contact')}
                    </UI.InputLabel>
                    <ContactPicker
                      participant={participant}
                      onChange={(participant) => setParticipant(participant)}
                      filters={{ event: message.event.id }}
                      nullable={false}
                    />
                  </UI.InputGroup>
                )}
              </>
            )}

            <UI.HR />

            <UI.Div sc={{ textAlign: 'right' }}>
              <UI.Button
                type="submit"
                disabled={!canSendEmail || sendMessageResult.loading}
                sc={{ brand: 'secondary', loading: sendMessageResult.loading }}
              >
                {sendNow === false ? t('schedule_message') : t('send_message')}
              </UI.Button>
            </UI.Div>
          </UI.FormGrid>
        </UI.Form>
      )}
    </>
  );
};

export default SendMessageForm;
